/**
 * OTP Input Component
 * 6-digit OTP input with auto-focus and paste support
 */

import React, { useRef, useEffect } from 'react';

const OtpInput = ({
  length = 6,
  value = '',
  onChange,
  error = false,
  disabled = false,
  autoFocus = true
}) => {
  const inputRefs = useRef([]);
  const otpArray = value.split('').concat(Array(length).fill('')).slice(0, length);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, digit) => {
    // Only allow digits
    if (digit && !/^\d$/.test(digit)) return;

    const newOtp = [...otpArray];
    newOtp[index] = digit;
    const newValue = newOtp.join('');

    onChange(newValue);

    // Auto-focus next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otpArray[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        handleChange(index, '');
      }
    }

    // Handle left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle right arrow
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only process if pasted data contains only digits
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, length);
    onChange(digits);

    // Focus the next empty input or last input
    const nextIndex = Math.min(digits.length, length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleFocus = (index) => {
    // Select the input value on focus for easier editing
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex justify-center gap-2">
      {otpArray.map((digit, index) => (
        <input
          key={index}
          ref={(ref) => (inputRefs.current[index] = ref)}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={`
            w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg
            transition-all duration-200 outline-none
            ${error
              ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200'
              : 'border-gray-300 focus:border-[#104a5f] focus:ring-2 focus:ring-[#104a5f]/20'
            }
            ${disabled
              ? 'bg-gray-100 cursor-not-allowed text-gray-400'
              : 'bg-white text-gray-900'
            }
            hover:border-gray-400 focus:scale-105
          `}
          aria-label={`Digit ${index + 1} of ${length}`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
